import { apiClient } from './client';
import type { RequestOptions } from '@/types/api';
import type {
  CertificateListResponse,
  CertificateDetailResponse,
  CreateCertificateRequest,
  CreateCertificateResponse,
  IssueCertificateRequest,
  IssueCertificateResponse,
  RenewCertificateResponse,
  SyncCertificateRequest,
  SyncCertificateResponse,
  CertificateFilters,
  CertificateContentResponse,
  CertificateParsedDetailsResponse,
  MonitorSummaryResponse,
} from '@/types/certificate';

/**
 * Certificates API functions for the CDN Manager
 */
export const certificatesApi = {
  /**
   * Fetch all certificates with optional filters
   */
  async getAll(filters?: CertificateFilters, options?: RequestOptions): Promise<CertificateListResponse> {
    const params: Record<string, string> = {};

    if (filters?.status) {
      params.status = filters.status;
    }
    if (filters?.expiring_soon) {
      params.expiring_soon = 'true';
    }
    if (filters?.domain_id) {
      params.domain_id = filters.domain_id;
    }
    if (filters?.node_id) {
      params.node_id = filters.node_id;
    }

    const response = await apiClient.get<CertificateListResponse>('/certificates', {
      params,
      signal: options?.signal,
    });
    return response.data;
  },

  /**
   * Fetch a single certificate by ID with related entities
   */
  async getById(id: string): Promise<CertificateDetailResponse> {
    const response = await apiClient.get<CertificateDetailResponse>(`/certificates/${id}`);
    return response.data;
  },

  /**
   * Create a certificate record for a domain (for importing from Plesk nodes)
   */
  async create(data: CreateCertificateRequest): Promise<CreateCertificateResponse> {
    const response = await apiClient.post<CreateCertificateResponse>('/certificates', data);
    return response.data;
  },

  /**
   * Issue a new certificate for a domain
   */
  async issue(data: IssueCertificateRequest): Promise<IssueCertificateResponse> {
    const response = await apiClient.post<IssueCertificateResponse>('/certificates/issue', data);
    return response.data;
  },

  /**
   * Renew an existing certificate
   */
  async renew(id: string): Promise<RenewCertificateResponse> {
    const response = await apiClient.post<RenewCertificateResponse>(`/certificates/${id}/renew`);
    return response.data;
  },

  /**
   * Retry a failed certificate issuance
   */
  async retry(id: string): Promise<IssueCertificateResponse> {
    const response = await apiClient.post<IssueCertificateResponse>(`/certificates/${id}/retry`);
    return response.data;
  },

  /**
   * Delete (revoke) a certificate
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/certificates/${id}`);
  },

  /**
   * Sync certificate content from source node
   */
  async sync(id: string, data?: SyncCertificateRequest): Promise<SyncCertificateResponse> {
    const response = await apiClient.post<SyncCertificateResponse>(`/certificates/${id}/sync`, data || {});
    return response.data;
  },

  /**
   * Get certificate content (without private key)
   */
  async getContent(id: string): Promise<CertificateContentResponse> {
    const response = await apiClient.get<CertificateContentResponse>(`/certificates/${id}/content`);
    return response.data;
  },

  /**
   * Get parsed X.509 certificate details
   */
  async getParsedDetails(id: string, options?: RequestOptions): Promise<CertificateParsedDetailsResponse> {
    const response = await apiClient.get<CertificateParsedDetailsResponse>(`/certificates/${id}/details`, {
      signal: options?.signal,
    });
    return response.data;
  },

  /**
   * Trigger certificate monitoring - check all certificates on nodes for updates
   */
  async triggerMonitoring(): Promise<MonitorSummaryResponse> {
    const response = await apiClient.post<MonitorSummaryResponse>('/certificates/monitor');
    return response.data;
  },
};
